import { useState } from "react";
import { useClientes } from "../lib/clientes";
import { useDebounce } from "../lib/use-debounce";
import { Campo, Entrada, Modal } from "./ui";

export interface ClienteResumen {
  id: string;
  rfc: string;
  razonSocial: string;
}

interface Props {
  valor: ClienteResumen | null;
  alSeleccionar: (cliente: ClienteResumen) => void;
  error?: string;
}

/**
 * Selector por búsqueda en un modal, no un `<select>` con todos los clientes:
 * con unas cuantas decenas de clientes activos un desplegable se vuelve
 * inmanejable, y aquí ya se busca por RFC o razón social igual que en la
 * pantalla de clientes.
 */
export function SelectorCliente({ valor, alSeleccionar, error }: Props) {
  const [abierto, setAbierto] = useState(false);

  return (
    <>
      <Campo etiqueta="Cliente" requerido error={error}>
        <button
          type="button"
          onClick={() => setAbierto(true)}
          className={`w-full rounded-md border px-3 py-2 text-left text-sm transition ${
            error
              ? "border-red-400 dark:border-red-700"
              : "border-slate-300 hover:border-slate-400 dark:border-slate-700 dark:hover:border-slate-600"
          } bg-white dark:bg-slate-950`}
        >
          {valor ? (
            <>
              <span className="font-medium">{valor.razonSocial}</span>
              <span className="ml-2 font-mono text-xs text-slate-500">{valor.rfc}</span>
            </>
          ) : (
            <span className="text-slate-400">Selecciona un cliente…</span>
          )}
        </button>
      </Campo>

      <Modal
        titulo="Seleccionar cliente"
        abierto={abierto}
        alCerrar={() => setAbierto(false)}
        ancho="max-w-lg"
      >
        <BuscadorClientes
          alSeleccionar={(c) => {
            alSeleccionar(c);
            setAbierto(false);
          }}
        />
      </Modal>
    </>
  );
}

function BuscadorClientes({
  alSeleccionar,
}: {
  alSeleccionar: (c: ClienteResumen) => void;
}) {
  const [texto, setTexto] = useState("");
  const busqueda = useDebounce(texto, 300);
  const { data, isLoading } = useClientes({ q: busqueda || undefined, activo: true });

  return (
    <div className="space-y-3 p-5">
      <Entrada
        autoFocus
        value={texto}
        onChange={(e) => setTexto(e.target.value)}
        placeholder="Buscar por RFC o razón social"
      />
      <div className="max-h-96 divide-y divide-slate-100 overflow-y-auto rounded-md border border-slate-200 dark:divide-slate-800 dark:border-slate-800">
        {isLoading && (
          <p className="px-3 py-6 text-center text-sm text-slate-500">Buscando…</p>
        )}
        {!isLoading &&
          (data ?? []).map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => alSeleccionar(c)}
              className="block w-full px-3 py-2 text-left transition hover:bg-slate-50 dark:hover:bg-slate-800/50"
            >
              <div className="text-sm font-medium">{c.razonSocial}</div>
              <div className="font-mono text-xs text-slate-500">{c.rfc}</div>
            </button>
          ))}
        {!isLoading && (data ?? []).length === 0 && (
          <p className="px-3 py-6 text-center text-sm text-slate-500">
            {texto ? "Sin coincidencias." : "Sin clientes activos."}
          </p>
        )}
      </div>
    </div>
  );
}
